'use client';

import { Download, FlaskConical, Loader2 } from 'lucide-react';
import { useState } from 'react';

type Mode = 'year' | 'last' | 'custom';

function lastCompleteMonth(): string {
	const d = new Date();
	d.setDate(1);
	d.setMonth(d.getMonth() - 1);
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function addMonths(ym: string, n: number): string {
	const [y, m] = ym.split('-').map(Number);
	const d = new Date(Date.UTC(y!, m! - 1 + n, 1));
	return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export interface GseMockExportCardProps {
	busy: boolean;
	onExport: (from: string, to: string) => void;
}

/**
 * Esporta un CSV di misure simulate in stile GSE (15 min, tutta la CER):
 * ultimo anno, ultimo mese completo o un mese specifico per colmare i buchi.
 * Le curve sono quelle della generazione predittiva (profili soci + FV/meteo).
 */
export function GseMockExportCard({ busy, onExport }: GseMockExportCardProps) {
	const lastMonth = lastCompleteMonth();
	const [mode, setMode] = useState<Mode>('year');
	const [customMonth, setCustomMonth] = useState(lastMonth);

	function handleDownload() {
		const to = mode === 'custom' ? customMonth : lastMonth;
		const from = mode === 'year' ? addMonths(lastMonth, -11) : to;
		onExport(from, to);
	}

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">
					<FlaskConical className="mr-1 inline h-3.5 w-3.5 text-accent-purple" />
					Simulazione misure GSE
				</span>
				<span className="card-badge badge-purple">DATI DI TEST</span>
			</div>

			<p className="mb-3 text-[12px] text-text-muted">
				Genera il CSV quartorario che il GSE pubblica per il Referente, simulato per tutti i POD
				della CER: prelievi dalle curve del profilo di consumo dei soci, immissioni da resa FV
				(PVGIS) e meteo storico, con energia condivisa per slot. Solo dati mock - nessuna lettura
				viene scritta.
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<select
					value={mode}
					onChange={(e) => setMode(e.target.value as Mode)}
					disabled={busy}
					aria-label="Periodo di esportazione"
					className="rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
				>
					<option value="year">Ultimo anno (12 mesi completi)</option>
					<option value="last">Ultimo mese completo</option>
					<option value="custom">Mese specifico…</option>
				</select>

				{mode === 'custom' && (
					<input
						type="month"
						value={customMonth}
						max={lastMonth}
						onChange={(e) => setCustomMonth(e.target.value)}
						disabled={busy}
						className="rounded border border-border bg-background px-2 py-1 text-[13px] text-text-primary"
					/>
				)}

				<button
					type="button"
					onClick={handleDownload}
					disabled={busy}
					className="inline-flex items-center gap-1.5 rounded border border-accent-purple/50 bg-accent-purple/10 px-3 py-1 text-[13px] text-accent-purple transition-colors hover:bg-accent-purple/20 disabled:opacity-50"
				>
					{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
					{busy ? 'Generazione in corso…' : 'Genera e scarica CSV'}
				</button>
			</div>

			<p className="mt-2 text-[11px] italic text-text-muted">
				L&apos;anno copre i 12 mesi fino all&apos;ultimo mese completo; per colmare un buco scegli
				il singolo mese. Un anno intero può richiedere qualche minuto.
			</p>
		</div>
	);
}
